<template>
  <div class="mt-100 cctv-page">

    <!-- ================= HERO HEADER ================= -->
    <div class="cctv-hero">
      <div class="cctv-hero-inner">
        <div class="cctv-hero-left">
          <div class="cctv-hero-title">
            SISTEM PEMANTAUAN CCTV<br />KABUPATEN TANGERANG
          </div>
          <div class="cctv-hero-tagline">Pantau Tangerang, Aman Bersama</div>
        </div>

        <div class="cctv-hero-logo">
          <img
            src="/images/logo-kab.png"
            alt="Logo Kabupaten Tangerang"
          />
          <div class="cctv-hero-logo-text">TANGERANG</div>
        </div>

        <div class="cctv-hero-clock">
          <div class="cctv-hero-time">
            <v-icon
              small
              color="#60a5fa"
            >mdi-clock-outline</v-icon>
            <span>{{ nowTime }} <small>WIB</small></span>
          </div>
          <div class="cctv-hero-date">{{ nowDate }}</div>
          <div class="cctv-hero-clock-divider"></div>
          <div class="cctv-hero-instansi">
            <v-icon
              small
              color="#60a5fa"
            >mdi-shield-account-outline</v-icon>
            <span>Diskominfo Kab. Tangerang</span>
          </div>
        </div>
      </div>
    </div>

    <div class="cctv-content">
      <!-- ================= ROW 1 : PETA + LIVE VIEW ================= -->
      <v-row dense>
        <v-col
          cols="12"
          :md="7"
          class="d-flex"
        >
          <div class="cctv-panel flex-grow-1">
            <div class="cctv-panel-header">
              <span class="cctv-panel-title">PETA SEBARAN CCTV</span>
              <v-spacer></v-spacer>
              <v-select
                v-model="selectedArea"
                :items="areaOptions"
                dense
                solo
                flat
                dark
                hide-details
                class="cctv-area-select"
                background-color="#0a1224"
                color="#60a5fa"
                append-icon="mdi-chevron-down"
              ></v-select>
            </div>

            <div class="cctv-map-wrapper">
              <l-map
                class="cctv-map"
                :style="{ height: device.mobile ? '380px' : '480px', zIndex: 1 }"
                :zoom="zoom"
                :center="center"
              >
                <l-tile-layer
                  :url="url"
                  :attribution="attribution"
                ></l-tile-layer>
                <l-marker
                  v-for="item in filteredMarkers"
                  :key="item.id"
                  :visible="item.visible"
                  :draggable="item.draggable"
                  :lat-lng.sync="item.position"
                  @click="openVideo(item)"
                >
                  <l-icon
                    :icon-url="item.icon"
                    :icon-size="item.size || [36, 48]"
                    :icon-anchor="item.anchor || [18, 46]"
                    :popup-anchor="[0, -40]"
                  />
                  <l-tooltip :content="item.tooltip" />
                </l-marker>

                <l-circle
                  v-if="selectedMarkerPosition"
                  :lat-lng="selectedMarkerPosition"
                  :radius="rippleRadiusPrimary"
                  color="#1e88e5"
                  :weight="2"
                  :opacity="rippleOpacityPrimary"
                  :fill-opacity="0"
                />
                <l-circle
                  v-if="selectedMarkerPosition"
                  :lat-lng="selectedMarkerPosition"
                  :radius="rippleRadiusSecondary"
                  color="#42a5f5"
                  :weight="2"
                  :opacity="rippleOpacitySecondary"
                  :fill-opacity="0"
                />
                <l-circle
                  v-if="selectedMarkerPosition"
                  :lat-lng="selectedMarkerPosition"
                  :radius="32"
                  color="#1565c0"
                  :weight="2"
                  :opacity="0.95"
                  :fill="true"
                  :fill-opacity="0.2"
                />
              </l-map>
            </div>

            <!-- statistik bawah peta -->
            <div class="cctv-map-stats">
              <div class="cctv-map-stat">
                <div class="cctv-map-stat-icon icon-total">
                  <v-icon
                    small
                    color="#60a5fa"
                  >mdi-cctv</v-icon>
                </div>
                <div>
                  <div class="cctv-map-stat-label">TOTAL KAMERA</div>
                  <div class="cctv-map-stat-value">{{ stats.total }}</div>
                  <div class="cctv-map-stat-sub">Unit</div>
                </div>
              </div>
              <div class="cctv-map-stat">
                <div class="cctv-map-stat-icon icon-online">
                  <v-icon
                    small
                    color="#34d399"
                  >mdi-wifi</v-icon>
                </div>
                <div>
                  <div class="cctv-map-stat-label">ONLINE</div>
                  <div class="cctv-map-stat-value online">{{ stats.online }}</div>
                  <div class="cctv-map-stat-sub">Unit ({{ percent(stats.online) }}%)</div>
                </div>
              </div>
              <div class="cctv-map-stat">
                <div class="cctv-map-stat-icon icon-offline">
                  <v-icon
                    small
                    color="#f87171"
                  >mdi-close-circle-outline</v-icon>
                </div>
                <div>
                  <div class="cctv-map-stat-label">OFFLINE</div>
                  <div class="cctv-map-stat-value offline">{{ stats.offline }}</div>
                  <div class="cctv-map-stat-sub">Unit ({{ percent(stats.offline) }}%)</div>
                </div>
              </div>
              <div class="cctv-map-stat">
                <div class="cctv-map-stat-icon icon-gangguan">
                  <v-icon
                    small
                    color="#fbbf24"
                  >mdi-alert-outline</v-icon>
                </div>
                <div>
                  <div class="cctv-map-stat-label">GANGGUAN</div>
                  <div class="cctv-map-stat-value gangguan">{{ stats.gangguan }}</div>
                  <div class="cctv-map-stat-sub">Unit</div>
                </div>
              </div>
            </div>
          </div>
        </v-col>

        <!-- LIVE VIEW -->
        <v-col
          cols="12"
          :md="5"
          class="d-flex"
        >
          <div class="cctv-panel flex-grow-1">
            <div class="cctv-panel-header">
              <span class="cctv-panel-title">LIVE VIEW</span>
              <v-spacer></v-spacer>
              <div
                class="cctv-live-dots"
                v-if="livePages > 1"
              >
                <v-icon
                  small
                  color="#94a3b8"
                  @click="prevLivePage"
                >mdi-chevron-left</v-icon>
                <span
                  v-for="p in livePages"
                  :key="p"
                  class="cctv-live-dot"
                  :class="{ 'cctv-live-dot--active': livePage === p - 1 }"
                  @click="livePage = p - 1"
                ></span>
                <v-icon
                  small
                  color="#94a3b8"
                  @click="nextLivePage"
                >mdi-chevron-right</v-icon>
              </div>
            </div>

            <div
              class="cctv-live-grid"
              v-if="liveCameras.length"
            >
              <div
                class="cctv-live-tile"
                v-for="item in liveCameras"
                :key="`live-${item.id}`"
                :class="{ 'cctv-live-tile--active': selectedMarkerId === item.id }"
                @mouseenter="focusCameraOnMap(item)"
                @click="openVideo(item)"
              >
                <img
                  :src="resolveThumbSource(item)"
                  :alt="item.name"
                  class="cctv-live-img"
                  :data-raw-src="item && item.lastCapture ? item.lastCapture : ''"
                  @error="onThumbError"
                />
                <div class="cctv-live-caption">
                  <span
                    class="cctv-live-status"
                    :class="item.kondisi === 'baik' ? 'on' : 'off'"
                  ></span>
                  <span class="cctv-live-name">{{ item.area }} - {{ item.name }}</span>
                  <v-icon
                    x-small
                    color="white"
                  >mdi-arrow-expand</v-icon>
                </div>
              </div>
            </div>
            <div
              v-else
              class="cctv-empty-list"
            >
              Data CCTV belum tersedia.
            </div>
          </div>
        </v-col>
      </v-row>

      <!-- ================= ROW 2 : STATISTIK / TOP 5 / PERISTIWA / PERANGKAT ================= -->
      <v-row dense>
        <!-- STATISTIK KAMERA -->
        <v-col
          cols="12"
          :md="3"
          class="d-flex"
        >
          <div class="cctv-panel flex-grow-1">
            <div class="cctv-panel-header">
              <span class="cctv-panel-title">STATISTIK KAMERA</span>
            </div>
            <div class="cctv-stat-body">
              <div
                class="cctv-donut"
                :style="donutStyle"
              >
                <div class="cctv-donut-hole">
                  <div class="cctv-donut-value">{{ stats.total }}</div>
                  <div class="cctv-donut-label">TOTAL</div>
                </div>
              </div>
              <div class="cctv-legend">
                <div class="cctv-legend-item">
                  <span
                    class="cctv-legend-dot"
                    style="background:#22c55e"
                  ></span>
                  Online
                  <b>{{ stats.online }} ({{ percent(stats.online) }}%)</b>
                </div>
                <div class="cctv-legend-item">
                  <span
                    class="cctv-legend-dot"
                    style="background:#ef4444"
                  ></span>
                  Offline
                  <b>{{ stats.offline }} ({{ percent(stats.offline) }}%)</b>
                </div>
                <div class="cctv-legend-item">
                  <span
                    class="cctv-legend-dot"
                    style="background:#f59e0b"
                  ></span>
                  Gangguan
                  <b>{{ stats.gangguan }} (0%)</b>
                </div>
              </div>
            </div>
          </div>
        </v-col>

        <!-- TOP 5 WILAYAH -->
        <v-col
          cols="12"
          :md="3"
          class="d-flex"
        >
          <div class="cctv-panel flex-grow-1">
            <div class="cctv-panel-header">
              <span class="cctv-panel-title">TOP 5 WILAYAH KAMERA TERBANYAK</span>
            </div>
            <div class="cctv-top-body">
              <div
                class="cctv-top-item"
                v-for="(area, idx) in topAreas"
                :key="area.name"
              >
                <div class="cctv-top-row">
                  <span class="cctv-top-rank">{{ idx + 1 }}.</span>
                  <span class="cctv-top-name">{{ area.name }}</span>
                  <span class="cctv-top-count">{{ area.count }} Unit</span>
                </div>
                <div class="cctv-top-bar">
                  <div
                    class="cctv-top-bar-fill"
                    :style="{ width: barWidth(area.count) }"
                  ></div>
                </div>
              </div>
              <div
                v-if="!topAreas.length"
                class="cctv-empty-list"
              >
                Belum ada data wilayah.
              </div>
            </div>
          </div>
        </v-col>

        <!-- PERISTIWA TERKINI -->
        <v-col
          cols="12"
          :md="3"
          class="d-flex"
        >
          <div class="cctv-panel flex-grow-1">
            <div class="cctv-panel-header">
              <span class="cctv-panel-title">PERISTIWA TERKINI</span>
            </div>
            <div class="cctv-event-body">
              <template v-if="events.length">
                <div
                  class="cctv-event-item"
                  v-for="(ev, idx) in events"
                  :key="idx"
                >
                  <div
                    class="cctv-event-icon"
                    :style="{ background: ev.bg }"
                  >
                    <v-icon
                      small
                      :color="ev.color"
                    >{{ ev.icon }}</v-icon>
                  </div>
                  <div class="cctv-event-text">
                    <div class="cctv-event-title">{{ ev.title }}</div>
                    <div class="cctv-event-subtitle">{{ ev.subtitle }}</div>
                  </div>
                </div>
              </template>
              <div
                v-else
                class="cctv-event-ok"
              >
                <v-icon
                  color="#34d399"
                  size="32"
                >mdi-check-circle-outline</v-icon>
                <span>Semua kamera berjalan normal</span>
              </div>
            </div>
          </div>
        </v-col>

        <!-- STATUS PERANGKAT -->
        <v-col
          cols="12"
          :md="3"
          class="d-flex"
        >
          <div class="cctv-panel flex-grow-1">
            <div class="cctv-panel-header">
              <span class="cctv-panel-title">STATUS PERANGKAT</span>
            </div>
            <div class="cctv-device-body">
              <div
                class="cctv-gauge"
                :style="gaugeStyle"
              >
                <div class="cctv-gauge-hole">
                  <div class="cctv-gauge-value">{{ percent(stats.online) }}%</div>
                  <div class="cctv-gauge-label">Online</div>
                </div>
              </div>
              <div class="cctv-device-stats">
                <div class="cctv-device-stat">
                  <div class="cctv-device-stat-value">{{ stats.total }}</div>
                  <div class="cctv-device-stat-label">Total Perangkat</div>
                </div>
                <div class="cctv-device-stat">
                  <div class="cctv-device-stat-value online">{{ stats.online }}</div>
                  <div class="cctv-device-stat-label">Online</div>
                </div>
                <div class="cctv-device-stat">
                  <div class="cctv-device-stat-value offline">{{ stats.offline }}</div>
                  <div class="cctv-device-stat-label">Offline</div>
                </div>
              </div>
            </div>
          </div>
        </v-col>
      </v-row>

      <!-- ================= STATUS BAR BAWAH ================= -->
      <div class="cctv-bottom-bar">
        <div class="cctv-bottom-item">
          <div
            class="cctv-bottom-icon"
            :style="{ background: stats.offline ? 'rgba(251, 191, 36, 0.15)' : 'rgba(52, 211, 153, 0.15)' }"
          >
            <v-icon
              small
              :color="stats.offline ? '#fbbf24' : '#34d399'"
            >{{ stats.offline ? "mdi-alert-outline" : "mdi-check-circle-outline" }}</v-icon>
          </div>
          <div>
            <div class="cctv-bottom-label">NOTIFIKASI SISTEM</div>
            <div class="cctv-bottom-value">
              {{ stats.offline ? stats.offline + " kamera sedang offline" : "Semua sistem berjalan normal" }}
            </div>
          </div>
        </div>
        <div class="cctv-bottom-item">
          <div
            class="cctv-bottom-icon"
            style="background:rgba(96, 165, 250, 0.15)"
          >
            <v-icon
              small
              color="#60a5fa"
            >mdi-map-marker-radius-outline</v-icon>
          </div>
          <div>
            <div class="cctv-bottom-label">WILAYAH TERPANTAU</div>
            <div class="cctv-bottom-value">{{ areaCountTotal }} Wilayah</div>
          </div>
        </div>
        <div class="cctv-bottom-item">
          <div
            class="cctv-bottom-icon"
            style="background:rgba(96, 165, 250, 0.15)"
          >
            <v-icon
              small
              color="#60a5fa"
            >mdi-earth</v-icon>
          </div>
          <div>
            <div class="cctv-bottom-label">KAMERA AKSES PUBLIK</div>
            <div class="cctv-bottom-value">{{ markers.length }} Kamera</div>
          </div>
        </div>
        <div class="cctv-bottom-item">
          <div
            class="cctv-bottom-icon"
            style="background:rgba(52, 211, 153, 0.15)"
          >
            <v-icon
              small
              color="#34d399"
            >mdi-shield-lock-outline</v-icon>
          </div>
          <div>
            <div class="cctv-bottom-label">KEAMANAN SISTEM</div>
            <div class="cctv-bottom-value aman">Aman</div>
          </div>
        </div>
      </div>
    </div>

    <!-- ================= DIALOG PANTAU CCTV ================= -->
    <v-dialog
      transition="dialog-bottom-transition"
      v-model="formcctv.show"
      :max-width="device.desktop ? `760px` : `100%`"
      persistent
      eager
      :fullscreen="device.mobile"
    >
      <v-card
        color="#101b36"
        dark
        class="pmt-dialog"
      >
        <div class="pmt-dialog-header">
          <span class="pmt-live-dot"></span>
          <span class="pmt-panel-title">{{ formcctv.record.name || "PANTAU CCTV" }}</span>
          <span
            class="pmt-panel-count"
            v-if="formcctv.record.area"
          >{{ formcctv.record.area }}</span>
          <v-spacer></v-spacer>
          <v-btn
            icon
            small
            @click="stopVideo"
          >
            <v-icon small>mdi-close</v-icon>
          </v-btn>
        </div>

        <div class="pmt-video-wrap">
          <video
            ref="videoPlayer"
            class="pmt-video"
            controls
            autoplay
            muted
            playsinline
          ></video>

          <div class="pmt-video-watermark">
            <div class="pmt-video-watermark-logos">
              <img
                src="/images/logo-kab.png"
                alt=""
                aria-hidden="true"
              />
              <img
                src="/images/logo-diskominfo-kabtangerang.jpeg"
                alt=""
                aria-hidden="true"
              />
            </div>
            <div class="pmt-video-watermark-text">
              Sistem Pemantauan CCTV Kab. Tangerang
            </div>
          </div>

          <div
            class="pmt-video-overlay"
            v-show="progressbar"
          >
            <v-progress-circular
              indeterminate
              color="#60a5fa"
              size="44"
            ></v-progress-circular>
            <span>Menghubungkan ke kamera...</span>
          </div>

          <div
            class="pmt-video-overlay"
            v-show="videoError"
          >
            <v-icon
              size="44"
              color="#f87171"
            >mdi-video-off-outline</v-icon>
            <span>Stream tidak tersedia, kamera mungkin offline</span>
          </div>

          <div
            class="pmt-video-overlay pmt-video-overlay--limit"
            v-show="watchLimitPaused"
          >
            <v-btn
              depressed
              dark
              color="#2563eb"
              class="mt-2"
              @click="continueWatch"
            >
              <v-icon
                left
                small
              >mdi-play</v-icon>
              Continue
            </v-btn>
          </div>

          <div
            class="pmt-watch-timer"
            v-if="formcctv.show && !progressbar && !videoError"
          >
            <v-icon
              x-small
              color="#93c5fd"
            >mdi-timer-outline</v-icon>
            {{ watchRemainLabel }}
          </div>
        </div>

        <v-divider class="pmt-dialog-divider"></v-divider>
        <v-card-actions class="pa-4">
          <div
            class="pmt-form-note"
            v-if="formcctv.record.address"
          >
            <v-icon
              x-small
              color="#94a3b8"
            >mdi-map-marker-outline</v-icon>
            {{ formcctv.record.address }}
          </div>
          <v-spacer></v-spacer>
          <v-btn
            text
            color="#94a3b8"
            @click="stopVideo"
          >Tutup</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>
  
  <script>
import { mapState, mapActions } from "vuex";
import {
  LMap,
  LTileLayer,
  LMarker,
  LCircle,
  LIcon,
  LTooltip,
} from "vue2-leaflet";
import "leaflet/dist/leaflet.css";
import Hls from "hls.js";
import { mapTile } from "../../../.env";

export default {
  name: "banner-component",
  components: {
    LMap,
    LTileLayer,
    LMarker,
    LCircle,
    LIcon,
    LTooltip,
  },
  props: {},
  data: () => ({
    //property map
    url: mapTile.URL,
    attribution: mapTile.ATTRIBUTION,
    zoom: 11,
    center: [-6.2204, 106.4675],

    progressbar: false,
    hls: null,
    cctvLoadingOnWaiting: null,
    cctvLoadingOnStalled: null,
    cctvLoadingOnCanPlay: null,
    cctvLoadingOnPlaying: null,
    captureUploadAt: {},
    captureUploadInFlight: {},
    activeCctvId: null,
    pendingInitTimer: null,
    markers: [],
    selectedMarkerId: null,
    selectedMarkerPosition: null,
    rippleStep: 0,
    rippleTimer: null,
    videoError: false,
    watchLimitPaused: false,
    watchLimitMs: 5 * 60 * 1000,
    watchRemainMs: 5 * 60 * 1000,
    watchLimitTimer: null,
    watchTickTimer: null,

    selectedArea: "Semua Wilayah",
    livePage: 0,
    livePerPage: 6,

    now: new Date(),
    clockTimer: null,

    formcctv: {
      show: false,
      record: {
        name: "",
        area: "",
        address: "",
        status: "",
      },
    },
  }),
  mounted() {
    this.setPage({
      crud: false,
    });
    this.fetchCctv();
    this.startRippleAnimation();
    this.clockTimer = setInterval(() => {
      this.now = new Date();
    }, 1000);
  },
  beforeDestroy() {
    if (this.pendingInitTimer) {
      clearTimeout(this.pendingInitTimer);
      this.pendingInitTimer = null;
    }
    this.clearWatchLimit();
    this.destroyPlayer();
    if (this.rippleTimer) {
      clearInterval(this.rippleTimer);
      this.rippleTimer = null;
    }
    if (this.clockTimer) {
      clearInterval(this.clockTimer);
      this.clockTimer = null;
    }
  },

  computed: {
    ...mapState(["device", "http", "info", "theme"]),
    watchRemainLabel() {
      if (this.watchLimitPaused) return "Dijeda";
      const total = Math.max(0, Math.ceil(this.watchRemainMs / 1000));
      const m = Math.floor(total / 60);
      const s = String(total % 60).padStart(2, "0");
      return `${m}:${s}`;
    },
    nowTime() {
      return this.now.toLocaleTimeString("id-ID", {
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
      });
    },
    nowDate() {
      return this.now.toLocaleDateString("id-ID", {
        weekday: "long",
        day: "numeric",
        month: "long",
        year: "numeric",
      });
    },
    areaOptions() {
      const names = [];
      this.markers.forEach((item) => {
        if (item.area && names.indexOf(item.area) === -1) {
          names.push(item.area);
        }
      });
      names.sort();
      return ["Semua Wilayah"].concat(names);
    },
    filteredMarkers() {
      if (!this.selectedArea || this.selectedArea === "Semua Wilayah") {
        return this.markers;
      }
      return this.markers.filter((item) => item.area === this.selectedArea);
    },
    stats() {
      const total = this.filteredMarkers.length;
      const online = this.filteredMarkers.filter(
        (item) => item.kondisi === "baik"
      ).length;
      return {
        total,
        online,
        offline: total - online,
        gangguan: 0,
      };
    },
    topAreas() {
      const counts = {};
      this.markers.forEach((item) => {
        const name = item.area || "Lainnya";
        counts[name] = (counts[name] || 0) + 1;
      });
      return Object.keys(counts)
        .map((name) => ({ name, count: counts[name] }))
        .sort((a, b) => b.count - a.count)
        .slice(0, 5);
    },
    areaCountTotal() {
      const names = [];
      this.markers.forEach((item) => {
        if (item.area && names.indexOf(item.area) === -1) names.push(item.area);
      });
      return names.length;
    },
    events() {
      return this.markers
        .filter((item) => item.kondisi !== "baik")
        .slice(0, 5)
        .map((item) => ({
          icon: "mdi-video-off-outline",
          color: "#f87171",
          bg: "rgba(248, 113, 113, 0.15)",
          title: "Kamera Offline",
          subtitle: (item.area ? item.area + " - " : "") + item.name,
        }));
    },
    livePages() {
      return Math.max(
        1,
        Math.ceil(this.filteredMarkers.length / this.livePerPage)
      );
    },
    liveCameras() {
      const start = this.livePage * this.livePerPage;
      return this.filteredMarkers.slice(start, start + this.livePerPage);
    },
    donutStyle() {
      const total = this.stats.total;
      if (!total) {
        return { background: "#1e2c4f" };
      }
      const onlineDeg = (this.stats.online / total) * 360;
      return {
        background: `conic-gradient(#22c55e 0deg ${onlineDeg}deg, #ef4444 ${onlineDeg}deg 360deg)`,
      };
    },
    gaugeStyle() {
      const total = this.stats.total;
      const pct = total ? this.stats.online / total : 0;
      const deg = pct * 360;
      return {
        background: `conic-gradient(#34d399 0deg ${deg}deg, #1e2c4f ${deg}deg 360deg)`,
      };
    },
    rippleRadiusPrimary() {
      return 60 + this.rippleStep * 6;
    },
    rippleRadiusSecondary() {
      const phase = (this.rippleStep + 50) % 100;
      return 60 + phase * 6;
    },
    rippleOpacityPrimary() {
      return Math.max(0.08, 0.55 - this.rippleStep * 0.005);
    },
    rippleOpacitySecondary() {
      const phase = (this.rippleStep + 50) % 100;
      return Math.max(0.08, 0.55 - phase * 0.005);
    },
  },
  watch: {
    selectedArea() {
      this.livePage = 0;
      if (
        this.selectedArea !== "Semua Wilayah" &&
        this.filteredMarkers.length
      ) {
        const first = this.filteredMarkers[0];
        if (first && first.position) {
          this.center = [first.position.lat, first.position.lng];
          this.zoom = 13;
        }
      } else {
        this.center = [-6.2204, 106.4675];
        this.zoom = 11;
      }
    },
  },
  methods: {
    ...mapActions(["setPage"]),
    percent(value) {
      if (!this.stats.total) return 0;
      return Math.round((value / this.stats.total) * 1000) / 10;
    },
    barWidth(count) {
      const max = this.topAreas.length ? this.topAreas[0].count : 1;
      return Math.round((count / max) * 100) + "%";
    },
    prevLivePage() {
      this.livePage = this.livePage > 0 ? this.livePage - 1 : this.livePages - 1;
    },
    nextLivePage() {
      this.livePage = this.livePage < this.livePages - 1 ? this.livePage + 1 : 0;
    },
    fetchCctv: async function () {
      try {
        let { data } = await this.http.get("cctv-lists");
        this.markers = (Array.isArray(data) ? data : []).map((item) => ({
          ...item,
          lastCapture:
            item &&
            (item.lastCapture || item.foto || item.capture || item.thumbnail)
              ? item.lastCapture || item.foto || item.capture || item.thumbnail
              : "",
        }));
      } catch (error) {}
    },
    resolveStreamCandidates: function (itemId) {
      const baseURL =
        (this.http && this.http.defaults && this.http.defaults.baseURL) || "";
      const normalizedBase = String(baseURL).replace(/\/$/, "");
      const streamPath = `/stream-cctv/${encodeURIComponent(
        itemId
      )}/index.m3u8`;
      const urls = [];
      const pushUnique = function (u) {
        if (u && urls.indexOf(u) === -1) urls.push(u);
      };

      const origin =
        typeof window !== "undefined" ? window.location.origin : "";

      if (normalizedBase) {
        pushUnique(`${normalizedBase}${streamPath}`);
        const sansTrailingApi = normalizedBase.replace(/\/api$/i, "");
        pushUnique(`${sansTrailingApi}${streamPath}`);
        pushUnique(`${sansTrailingApi}/api${streamPath}`);
        const sansTrailingService = normalizedBase.replace(/\/service$/i, "");
        pushUnique(`${sansTrailingService}${streamPath}`);
        pushUnique(`${sansTrailingService}/service${streamPath}`);
      }

      pushUnique(`${origin}${streamPath}`);
      pushUnique(`${origin}/api${streamPath}`);
      pushUnique(`${origin}/service${streamPath}`);

      return urls.length ? urls : [`${origin}${streamPath}`];
    },
    resolveAssetBase: function () {
      const baseURL =
        (this.http && this.http.defaults && this.http.defaults.baseURL) || "";
      const normalizedBase = String(baseURL).replace(/\/$/, "");
      if (!normalizedBase) return window.location.origin;
      if (/\/api$/i.test(normalizedBase))
        return normalizedBase.replace(/\/api$/i, "");
      return normalizedBase;
    },
    resolveThumbSource: function (item) {
      const source =
        item && item.lastCapture ? String(item.lastCapture).trim() : "";
      const fallbackSvg =
        "data:image/svg+xml;utf8," +
        encodeURIComponent(
          '<svg xmlns="http://www.w3.org/2000/svg" width="320" height="180" viewBox="0 0 320 180"><defs><linearGradient id="g" x1="0" y1="0" x2="1" y2="1"><stop offset="0%" stop-color="#1d4ed8"/><stop offset="100%" stop-color="#2563eb"/></linearGradient></defs><rect width="320" height="180" fill="url(#g)"/><circle cx="160" cy="90" r="32" fill="none" stroke="#ffffff" stroke-width="7"/><circle cx="160" cy="90" r="8" fill="#ffffff"/></svg>'
        );
      if (!source) return fallbackSvg;
      const assetBase = this.resolveAssetBase();
      if (/^data:image\//i.test(source) || /^https?:\/\//i.test(source))
        return source;
      if (source.startsWith("/")) return `${assetBase}${source}`;
      return `${assetBase}/${source}`;
    },
    onThumbError: function (event) {
      if (
        event &&
        event.target &&
        event.target.dataset &&
        event.target.dataset.fallbackApplied === "1"
      ) {
        return;
      }
      if (event && event.target) {
        const rawSrc = String(event.target.dataset.rawSrc || "").trim();
        if (
          rawSrc &&
          rawSrc.startsWith("/") &&
          event.target.dataset.altTried !== "1"
        ) {
          event.target.dataset.altTried = "1";
          event.target.src = `${window.location.origin}${rawSrc}`;
          return;
        }
        event.target.dataset.fallbackApplied = "1";
        event.target.src =
          "data:image/svg+xml;utf8," +
          encodeURIComponent(
            '<svg xmlns="http://www.w3.org/2000/svg" width="320" height="180" viewBox="0 0 320 180"><defs><linearGradient id="g" x1="0" y1="0" x2="1" y2="1"><stop offset="0%" stop-color="#1d4ed8"/><stop offset="100%" stop-color="#2563eb"/></linearGradient></defs><rect width="320" height="180" fill="url(#g)"/><circle cx="160" cy="90" r="32" fill="none" stroke="#ffffff" stroke-width="7"/><circle cx="160" cy="90" r="8" fill="#ffffff"/></svg>'
          );
      }
    },
    openVideo: async function (item) {
      try {
        this.activeCctvId = item && item.id ? item.id : null;
        this.focusMarker(item);
        this.videoError = false;
        this.watchLimitPaused = false;
        this.formcctv.show = true;
        this.progressbar = true;
        this.formcctv.record.name = item.name;
        this.formcctv.record.area = item.area || "";
        this.formcctv.record.address = item.address;
        this.formcctv.record.status = item.status;
        await this.$nextTick();
        await this.initializeVideoPlayer(item);
      } catch (error) {
      } finally {
      }
    },
    stopVideo: function () {
      this.captureLastFrame(true);
      this.closeForm();
      this.destroyPlayer();
    },

    onSourceCompleted(source) {
      this.progressbar = false;
      if (!this.watchLimitPaused && !this.watchLimitTimer) {
        this.startWatchLimit();
      }
    },
    closeForm: function () {
      this.formcctv.show = false;
      this.progressbar = false;
      this.videoError = false;
      this.watchLimitPaused = false;
      this.clearWatchLimit();
      this.activeCctvId = null;
    },
    clearWatchLimit: function () {
      if (this.watchLimitTimer) {
        clearTimeout(this.watchLimitTimer);
        this.watchLimitTimer = null;
      }
      if (this.watchTickTimer) {
        clearInterval(this.watchTickTimer);
        this.watchTickTimer = null;
      }
    },
    startWatchLimit: function () {
      this.clearWatchLimit();
      this.watchLimitPaused = false;
      this.watchRemainMs = this.watchLimitMs;
      const startedAt = Date.now();
      this.watchTickTimer = setInterval(() => {
        this.watchRemainMs = Math.max(
          0,
          this.watchLimitMs - (Date.now() - startedAt)
        );
      }, 250);
      this.watchLimitTimer = setTimeout(() => {
        this.onWatchLimitReached();
      }, this.watchLimitMs);
    },
    onWatchLimitReached: function () {
      this.clearWatchLimit();
      this.watchRemainMs = 0;
      this.watchLimitPaused = true;
      const video = this.$refs.videoPlayer;
      if (video) {
        video.pause();
        video.controls = false;
      }
    },
    continueWatch: function () {
      this.watchLimitPaused = false;
      const video = this.$refs.videoPlayer;
      if (video) {
        video.controls = true;
        video.play().catch(() => {});
      }
      this.startWatchLimit();
    },
    focusMarker: function (item) {
      if (!item || !item.position) return;
      this.selectedMarkerId = item.id;
      this.center = [item.position.lat, item.position.lng];
      this.selectedMarkerPosition = {
        lat: item.position.lat,
        lng: item.position.lng,
      };
      this.zoom = 16;
    },
    /** Hover Live View: pan peta ke koordinat kamera (tanpa zoom) */
    focusCameraOnMap: function (item) {
      if (!item || !item.position) return;
      const lat = parseFloat(item.position.lat);
      const lng = parseFloat(item.position.lng);
      if (isNaN(lat) || isNaN(lng)) return;

      this.selectedMarkerId = item.id;
      this.selectedMarkerPosition = { lat, lng };
      this.center = [lat, lng];
      this.rippleStep = 0;
    },
    startRippleAnimation: function () {
      if (this.rippleTimer) return;
      this.rippleTimer = setInterval(() => {
        this.rippleStep = (this.rippleStep + 2) % 100;
      }, 80);
    },
    initializeVideoPlayer: async function (item) {
      const video = this.$refs.videoPlayer;
      if (!item || !item.id) {
        this.progressbar = false;
        return;
      }

      if (!video) {
        if (this.pendingInitTimer) {
          clearTimeout(this.pendingInitTimer);
        }
        this.pendingInitTimer = setTimeout(() => {
          this.pendingInitTimer = null;
          this.initializeVideoPlayer(item);
        }, 250);
        return;
      }

      if (this.pendingInitTimer) {
        clearTimeout(this.pendingInitTimer);
        this.pendingInitTimer = null;
      }

      if (!video || !item || !item.id) {
        this.progressbar = false;
        return;
      }

      this.destroyPlayer();

      await this.initializeHlsPlayer(item);
    },

    initializeHlsPlayer: async function (item) {
      const video = this.$refs.videoPlayer;
      if (!item || !item.id) {
        this.progressbar = false;
        return;
      }

      if (!video) {
        if (this.pendingInitTimer) {
          clearTimeout(this.pendingInitTimer);
        }
        this.pendingInitTimer = setTimeout(() => {
          this.pendingInitTimer = null;
          this.initializeHlsPlayer(item);
        }, 250);
        return;
      }

      if (this.pendingInitTimer) {
        clearTimeout(this.pendingInitTimer);
        this.pendingInitTimer = null;
      }

      if (!video || !item || !item.id) {
        this.progressbar = false;
        return;
      }

      this.destroyPlayer();
      this.videoError = false;
      const streamCandidates = this.resolveStreamCandidates(item.id);
      const baseUrl = streamCandidates[0] || "";
      if (!baseUrl) {
        this.progressbar = false;
        return;
      }
      const streamUrl = `${baseUrl}${
        baseUrl.includes("?") ? "&" : "?"
      }t=${Date.now()}`;

      if (Hls.isSupported()) {
        video.muted = true;
        this.hls = new Hls({
          enableWorker: true,
          lowLatencyMode: false,
          startFragPrefetch: true,
          initialLiveManifestSize: 3,
          maxBufferLength: 260,
          maxMaxBufferLength: 840,
          maxBufferSize: 110 * 1000 * 1000,
          maxFragLookUpTolerance: 0.75,
          maxBufferHole: 1.35,
          nudgeOffset: 0.25,
          nudgeMaxRetry: 40,
          nudgeOnVideoHole: true,
          detectStallWithCurrentTimeMs: 950,
          highBufferWatchdogPeriod: 1,
          backBufferLength: 280,
          liveSyncMode: "buffered",
          liveDurationInfinity: true,
          liveSyncDurationCount: 16,
          liveMaxLatencyDurationCount: 58,
          maxLiveSyncPlaybackRate: 1.02,
          stretchShortVideoTrack: false,
        });
        this.hls.loadSource(streamUrl);
        this.hls.attachMedia(video);

        // Minta segmen berikutnya lebih awal sebelum buffer menyusut di bawah ~1.5x target HLS.
        const prefetchLowWaterSec = 14;
        const prefetchMinGapMs = 950;
        if (this.cctvHlsPrefetchOnTimeupdate) {
          video.removeEventListener(
            "timeupdate",
            this.cctvHlsPrefetchOnTimeupdate
          );
        }
        if (this.cctvHlsPrefetchOnWaiting) {
          video.removeEventListener("waiting", this.cctvHlsPrefetchOnWaiting);
        }
        this._cctvHlsPrefetchKickAt = 0;
        this.cctvHlsPrefetchOnTimeupdate = () => {
          if (!this.hls || !video.buffered || video.buffered.length < 1) return;
          try {
            const end = video.buffered.end(video.buffered.length - 1);
            const ahead = end - video.currentTime;
            if (ahead > prefetchLowWaterSec) return;
            const now = Date.now();
            if (
              this._cctvHlsPrefetchKickAt &&
              now - this._cctvHlsPrefetchKickAt < prefetchMinGapMs
            ) {
              return;
            }
            this._cctvHlsPrefetchKickAt = now;
            this.hls.startLoad(-1);
          } catch (e) {}
        };
        this.cctvHlsPrefetchOnWaiting = () => {
          this.progressbar = true;
          if (this.hls) this.hls.startLoad(-1);
        };
        this.cctvLoadingOnWaiting = () => {
          this.progressbar = true;
        };
        this.cctvLoadingOnStalled = () => {
          this.progressbar = true;
        };
        this.cctvLoadingOnCanPlay = () => {
          this.progressbar = false;
        };
        this.cctvLoadingOnPlaying = () => {
          this.progressbar = false;
          if (!this.watchLimitPaused && !this.watchLimitTimer) {
            this.startWatchLimit();
          }
        };
        video.addEventListener("timeupdate", this.cctvHlsPrefetchOnTimeupdate);
        video.addEventListener("waiting", this.cctvHlsPrefetchOnWaiting);
        video.addEventListener("waiting", this.cctvLoadingOnWaiting);
        video.addEventListener("stalled", this.cctvLoadingOnStalled);
        video.addEventListener("canplay", this.cctvLoadingOnCanPlay);
        video.addEventListener("playing", this.cctvLoadingOnPlaying);
        this.hls.on(Hls.Events.MANIFEST_PARSED, () => {
          this.progressbar = true;
          video.play().catch(() => {});
        });
        video.addEventListener(
          "canplay",
          () => {
            video.play().catch(() => {});
          },
          { once: true }
        );
        this.hls.on(Hls.Events.BUFFER_APPENDED, () => {
          if (video.readyState >= 3) {
            this.onSourceCompleted();
          }
        });
        this.hls.on(Hls.Events.ERROR, (_event, data) => {
          if (!data || !data.fatal) {
            return;
          }

          // Recover otomatis saat network/media error untuk mengurangi stuck loading.
          if (data.type === Hls.ErrorTypes.NETWORK_ERROR) {
            this.hls.startLoad();
            return;
          }
          if (data.type === Hls.ErrorTypes.MEDIA_ERROR) {
            this.hls.recoverMediaError();
            return;
          }
          video.play().catch(() => {});
          this.progressbar = false;
          this.videoError = true;
        });
      } else if (video.canPlayType("application/vnd.apple.mpegurl")) {
        video.src = streamUrl;
        video.addEventListener(
          "waiting",
          () => {
            this.progressbar = true;
          },
          { once: false }
        );
        video.addEventListener(
          "playing",
          () => {
            this.progressbar = false;
          },
          { once: false }
        );
        video.addEventListener(
          "loadedmetadata",
          () => {
            this.onSourceCompleted();
            video.play().catch(() => {});
          },
          { once: true }
        );
        video.addEventListener(
          "error",
          () => {
            this.progressbar = false;
            this.videoError = true;
          },
          { once: true }
        );
      } else {
        this.progressbar = false;
      }
    },
    uploadCaptureToBackend: async function (itemId, imageData, force = false) {
      if (!itemId || !imageData) return;
      if (this.captureUploadInFlight[itemId]) return;

      const now = Date.now();
      const lastUploadAt = this.captureUploadAt[itemId] || 0;
      // Batas upload berkala supaya tidak spam DB saat capture timer jalan.
      if (!force && now - lastUploadAt < 20000) return;

      this.captureUploadInFlight = {
        ...this.captureUploadInFlight,
        [itemId]: true,
      };
      try {
        const candidates = this.resolveStreamCandidates(itemId).map((url) =>
          url.replace("/index.m3u8", "/capture")
        );
        for (const endpoint of candidates) {
          try {
            const resp = await fetch(endpoint, {
              method: "POST",
              headers: {
                "Content-Type": "application/json",
              },
              body: JSON.stringify({ image: imageData }),
            });
            if (resp.ok) {
              this.captureUploadAt = {
                ...this.captureUploadAt,
                [itemId]: now,
              };
              try {
                const body = await resp.json();
                const foto =
                  body &&
                  body.response &&
                  body.response.data &&
                  body.response.data.foto;
                if (foto) {
                  const durable = `${foto}?t=${now}`;
                  this.markers = this.markers.map((item) =>
                    item && item.id === itemId
                      ? { ...item, lastCapture: durable, foto: durable }
                      : item
                  );
                }
              } catch (e) {}
              break;
            }
          } catch (error) {}
        }
      } finally {
        this.captureUploadInFlight = {
          ...this.captureUploadInFlight,
          [itemId]: false,
        };
      }
    },
    captureLastFrame: function (forceUpload = false) {
      const itemId = this.activeCctvId;
      const video = this.$refs.videoPlayer;
      if (
        !itemId ||
        !video ||
        video.readyState < 2 ||
        !video.videoWidth ||
        !video.videoHeight
      ) {
        return;
      }

      try {
        const maxWidth = 640;
        const scale = Math.min(1, maxWidth / video.videoWidth);
        const outputWidth = Math.max(1, Math.floor(video.videoWidth * scale));
        const outputHeight = Math.max(1, Math.floor(video.videoHeight * scale));
        const canvas = document.createElement("canvas");
        canvas.width = outputWidth;
        canvas.height = outputHeight;
        const ctx = canvas.getContext("2d");
        if (!ctx) return;
        ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
        const dataUrl = canvas.toDataURL("image/jpeg", 0.65);
        this.markers = this.markers.map((item) =>
          item && item.id === itemId ? { ...item, lastCapture: dataUrl } : item
        );
        this.uploadCaptureToBackend(itemId, dataUrl, forceUpload);
      } catch (error) {}
    },
    destroyPlayer: function () {
      this.clearWatchLimit();
      const video = this.$refs.videoPlayer;
      if (video && this.cctvHlsPrefetchOnTimeupdate) {
        video.removeEventListener(
          "timeupdate",
          this.cctvHlsPrefetchOnTimeupdate
        );
        this.cctvHlsPrefetchOnTimeupdate = null;
      }
      if (video && this.cctvHlsPrefetchOnWaiting) {
        video.removeEventListener("waiting", this.cctvHlsPrefetchOnWaiting);
        this.cctvHlsPrefetchOnWaiting = null;
      }
      if (video && this.cctvLoadingOnWaiting) {
        video.removeEventListener("waiting", this.cctvLoadingOnWaiting);
        this.cctvLoadingOnWaiting = null;
      }
      if (video && this.cctvLoadingOnStalled) {
        video.removeEventListener("stalled", this.cctvLoadingOnStalled);
        this.cctvLoadingOnStalled = null;
      }
      if (video && this.cctvLoadingOnCanPlay) {
        video.removeEventListener("canplay", this.cctvLoadingOnCanPlay);
        this.cctvLoadingOnCanPlay = null;
      }
      if (video && this.cctvLoadingOnPlaying) {
        video.removeEventListener("playing", this.cctvLoadingOnPlaying);
        this.cctvLoadingOnPlaying = null;
      }
      this._cctvHlsPrefetchKickAt = 0;
      if (this.hls) {
        this.hls.destroy();
        this.hls = null;
      }
      if (video) {
        video.pause();
        if (video.srcObject) {
          try {
            video.srcObject.getTracks().forEach((t) => t.stop());
          } catch (e) {}
          video.srcObject = null;
        }
        video.removeAttribute("src");
        video.load();
      }
    },
  },
};
</script>
  
  <style>
.cctv-page {
  background: linear-gradient(180deg, #0a1224 0%, #0c1530 100%);
  position: relative;
  padding-bottom: 24px;
}

/* samakan latar aplikasi dengan tema gelap saat halaman ini tampil
   (menutup celah putih di margin atas footer) */
.v-application:has(.cctv-page),
.v-application:has(.cctv-page) .v-main {
  background: #0c1530 !important;
}

/* ================= HERO ================= */
.cctv-hero {
  position: relative;
  background:
    linear-gradient(90deg, rgba(10, 18, 36, 0.95) 0%, rgba(13, 23, 48, 0.88) 55%, rgba(16, 27, 54, 0.82) 100%),
    url("/images/bg-kota-tangerang.png") center/cover no-repeat;
  border-bottom: 1px solid #1e2c4f;
}

.cctv-hero-inner {
  display: flex;
  align-items: center;
  gap: 24px;
  max-width: 1400px;
  margin: 0 auto;
  padding: 26px 24px;
}

.cctv-hero-left {
  flex: 1;
}

.cctv-hero-title {
  font-size: 28px;
  line-height: 1.2;
  font-weight: 900;
  letter-spacing: 0.5px;
  color: #e2e8f0;
  text-shadow: 0 2px 12px rgba(2, 6, 23, 0.8);
}

.cctv-hero-tagline {
  margin-top: 8px;
  font-size: 14px;
  font-weight: 600;
  color: #93c5fd;
  opacity: 0.95;
}

.cctv-hero-logo {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
}

.cctv-hero-logo img {
  height: 84px;
  filter: drop-shadow(0 4px 14px rgba(96, 165, 250, 0.35));
}

.cctv-hero-logo-text {
  font-size: 11px;
  font-weight: 800;
  letter-spacing: 2px;
  color: #93c5fd;
}

.cctv-hero-clock {
  background: rgba(16, 27, 54, 0.85);
  border: 1px solid #1e2c4f;
  border-radius: 14px;
  padding: 14px 18px;
  min-width: 220px;
  box-shadow: 0 10px 26px rgba(2, 6, 23, 0.45);
  backdrop-filter: blur(4px);
}

.cctv-hero-time {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 20px;
  font-weight: 800;
  color: #e2e8f0;
}

.cctv-hero-time small {
  font-size: 11px;
  font-weight: 700;
  color: #94a3b8;
}

.cctv-hero-date {
  margin-top: 2px;
  font-size: 12px;
  color: #94a3b8;
}

.cctv-hero-clock-divider {
  height: 1px;
  background: #1e2c4f;
  margin: 10px 0;
}

.cctv-hero-instansi {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 12px;
  font-weight: 700;
  color: #93c5fd;
}

/* ================= KONTEN ================= */
.cctv-content {
  max-width: 1400px;
  margin: 0 auto;
  padding: 16px 16px 0 16px;
}

.cctv-panel {
  background: #101b36;
  border: 1px solid #1e2c4f;
  border-radius: 14px;
  overflow: hidden;
  box-shadow: 0 10px 26px rgba(2, 6, 23, 0.4);
  display: flex;
  flex-direction: column;
}

.cctv-panel-header {
  display: flex;
  align-items: center;
  padding: 12px 16px;
  border-bottom: 1px solid #1e2c4f;
}

.cctv-panel-title {
  font-size: 12px;
  font-weight: 800;
  letter-spacing: 0.8px;
  color: #e2e8f0;
}

.cctv-area-select {
  max-width: 200px;
  border-radius: 8px;
  font-size: 13px;
}

.cctv-area-select .v-input__slot {
  border: 1px solid #1e2c4f !important;
  min-height: 34px !important;
}

/* peta */
.cctv-map-wrapper {
  width: 100%;
  overflow: hidden;
}

.cctv-map {
  z-index: 0;
}

.cctv-map-stats {
  display: flex;
  gap: 10px;
  padding: 12px;
  border-top: 1px solid #1e2c4f;
  flex-wrap: wrap;
}

.cctv-map-stat {
  flex: 1;
  min-width: 130px;
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 12px;
  border-radius: 12px;
  background: #0d1730;
  border: 1px solid #1e2c4f;
}

.cctv-map-stat-icon {
  width: 36px;
  height: 36px;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.icon-total {
  background: rgba(96, 165, 250, 0.15);
}

.icon-online {
  background: rgba(52, 211, 153, 0.15);
}

.icon-offline {
  background: rgba(248, 113, 113, 0.15);
}

.icon-gangguan {
  background: rgba(251, 191, 36, 0.15);
}

.cctv-map-stat-label {
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.5px;
  color: #94a3b8;
}

.cctv-map-stat-value {
  font-size: 20px;
  font-weight: 900;
  line-height: 1.1;
  color: #e2e8f0;
}

.cctv-map-stat-value.online {
  color: #34d399;
}

.cctv-map-stat-value.offline {
  color: #f87171;
}

.cctv-map-stat-value.gangguan {
  color: #fbbf24;
}

.cctv-map-stat-sub {
  font-size: 10px;
  color: #64748b;
}

/* live view */
.cctv-live-dots {
  display: flex;
  align-items: center;
  gap: 6px;
}

.cctv-live-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: #334155;
  cursor: pointer;
  transition: background 0.2s ease;
}

.cctv-live-dot--active {
  background: #60a5fa;
}

.cctv-live-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 10px;
  padding: 12px;
}

.cctv-live-tile {
  position: relative;
  aspect-ratio: 16 / 10;
  border-radius: 10px;
  overflow: hidden;
  cursor: pointer;
  background: linear-gradient(160deg, #0e1830 0%, #122142 100%);
  border: 1px solid #1e2c4f;
  transition: transform 0.2s ease, box-shadow 0.2s ease, border-color 0.2s ease;
}

.cctv-live-tile:hover {
  transform: translateY(-2px);
  border-color: #3b82f6;
  box-shadow: 0 10px 22px rgba(2, 6, 23, 0.55);
}

.cctv-live-tile--active {
  border-color: #60a5fa;
  box-shadow: 0 0 0 1px rgba(96, 165, 250, 0.45), 0 10px 22px rgba(2, 6, 23, 0.55);
}

.cctv-live-img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
}

.cctv-live-caption {
  position: absolute;
  left: 0;
  right: 0;
  bottom: 0;
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 6px 9px;
  background: linear-gradient(0deg, rgba(2, 6, 23, 0.85) 0%, rgba(2, 6, 23, 0) 100%);
}

.cctv-live-status {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  flex-shrink: 0;
}

.cctv-live-status.on {
  background: #22c55e;
  box-shadow: 0 0 6px #22c55e;
}

.cctv-live-status.off {
  background: #ef4444;
  box-shadow: 0 0 6px #ef4444;
}

.cctv-live-name {
  flex: 1;
  font-size: 11px;
  font-weight: 600;
  color: #ffffff;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

/* statistik kamera */
.cctv-stat-body {
  display: flex;
  align-items: center;
  gap: 18px;
  padding: 18px 16px;
}

.cctv-donut {
  position: relative;
  width: 120px;
  height: 120px;
  border-radius: 50%;
  flex-shrink: 0;
}

.cctv-donut-hole {
  position: absolute;
  inset: 16px;
  border-radius: 50%;
  background: #101b36;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
}

.cctv-donut-value {
  font-size: 24px;
  font-weight: 900;
  color: #e2e8f0;
  line-height: 1;
}

.cctv-donut-label {
  font-size: 9px;
  font-weight: 700;
  letter-spacing: 1px;
  color: #64748b;
}

.cctv-legend {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.cctv-legend-item {
  display: flex;
  align-items: center;
  gap: 7px;
  font-size: 12px;
  color: #94a3b8;
}

.cctv-legend-item b {
  color: #e2e8f0;
}

.cctv-legend-dot {
  width: 9px;
  height: 9px;
  border-radius: 50%;
  flex-shrink: 0;
}

/* top 5 wilayah */
.cctv-top-body {
  padding: 14px 16px;
  display: flex;
  flex-direction: column;
  gap: 11px;
}

.cctv-top-row {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 12px;
}

.cctv-top-rank {
  font-weight: 800;
  color: #93c5fd;
}

.cctv-top-name {
  flex: 1;
  font-weight: 600;
  color: #cbd5e1;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.cctv-top-count {
  font-size: 11px;
  color: #94a3b8;
}

.cctv-top-bar {
  margin-top: 4px;
  height: 6px;
  border-radius: 999px;
  background: #0a1224;
  overflow: hidden;
}

.cctv-top-bar-fill {
  height: 100%;
  border-radius: 999px;
  background: linear-gradient(90deg, #1d4ed8 0%, #60a5fa 100%);
}

/* peristiwa */
.cctv-event-body {
  padding: 12px 14px;
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.cctv-event-item {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 8px 10px;
  border-radius: 10px;
  background: #0d1730;
  border: 1px solid #1e2c4f;
}

.cctv-event-icon {
  width: 32px;
  height: 32px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.cctv-event-title {
  font-size: 12px;
  font-weight: 700;
  color: #e2e8f0;
}

.cctv-event-subtitle {
  font-size: 11px;
  color: #94a3b8;
}

.cctv-event-ok {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  padding: 26px 12px;
  font-size: 12px;
  font-weight: 600;
  color: #34d399;
  text-align: center;
}

/* status perangkat */
.cctv-device-body {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 14px;
  padding: 18px 16px;
}

.cctv-gauge {
  position: relative;
  width: 110px;
  height: 110px;
  border-radius: 50%;
}

.cctv-gauge-hole {
  position: absolute;
  inset: 12px;
  border-radius: 50%;
  background: #101b36;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
}

.cctv-gauge-value {
  font-size: 22px;
  font-weight: 900;
  color: #34d399;
  line-height: 1;
}

.cctv-gauge-label {
  font-size: 10px;
  font-weight: 700;
  color: #64748b;
}

.cctv-device-stats {
  display: flex;
  gap: 16px;
  width: 100%;
  justify-content: space-around;
}

.cctv-device-stat {
  text-align: center;
}

.cctv-device-stat-value {
  font-size: 18px;
  font-weight: 900;
  color: #e2e8f0;
}

.cctv-device-stat-value.online {
  color: #34d399;
}

.cctv-device-stat-value.offline {
  color: #f87171;
}

.cctv-device-stat-label {
  font-size: 10px;
  color: #94a3b8;
}

/* status bar bawah */
.cctv-bottom-bar {
  display: flex;
  gap: 12px;
  margin-top: 8px;
  padding: 12px 16px;
  background: #0d1730;
  border: 1px solid #1e2c4f;
  border-radius: 14px;
  flex-wrap: wrap;
}

.cctv-bottom-item {
  flex: 1;
  min-width: 200px;
  display: flex;
  align-items: center;
  gap: 10px;
}

.cctv-bottom-icon {
  width: 34px;
  height: 34px;
  border-radius: 9px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.cctv-bottom-label {
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.5px;
  color: #64748b;
}

.cctv-bottom-value {
  font-size: 12px;
  font-weight: 600;
  color: #e2e8f0;
}

.cctv-bottom-value.aman {
  color: #4ade80;
}

.cctv-empty-list {
  text-align: center;
  font-size: 13px;
  color: #64748b;
  padding: 18px 8px;
}

/* ================= DIALOG (sama seperti pemantauan) ================= */
.pmt-dialog {
  border: 1px solid #1e2c4f;
}

.pmt-dialog-header {
  display: flex;
  align-items: center;
  padding: 14px 16px;
  border-bottom: 1px solid #1e2c4f;
  background: #0d1730;
}

.pmt-dialog-divider {
  border-color: #1e2c4f !important;
}

.pmt-panel-title {
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.8px;
  color: #e2e8f0;
}

.pmt-panel-count {
  margin-left: 10px;
  padding: 2px 10px;
  border-radius: 999px;
  background: #0a1224;
  border: 1px solid #1e2c4f;
  font-size: 10px;
  font-weight: 600;
  color: #93c5fd;
  white-space: nowrap;
}

.pmt-form-note {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 11px;
  color: #94a3b8;
}

.pmt-live-dot {
  width: 9px;
  height: 9px;
  border-radius: 50%;
  background: #ef4444;
  box-shadow: 0 0 10px #ef4444;
  margin-right: 10px;
  animation: pmt-pulse 1.2s ease-in-out infinite;
}

@keyframes pmt-pulse {
  0%,
  100% {
    opacity: 1;
  }
  50% {
    opacity: 0.35;
  }
}

.pmt-video-wrap {
  position: relative;
  background: #020617;
  aspect-ratio: 16 / 9;
}

.pmt-video {
  width: 100%;
  height: 100%;
  display: block;
  object-fit: contain;
  background: #020617;
}

.pmt-video-watermark {
  position: absolute;
  left: 50%;
  top: 50%;
  transform: translate(-50%, -50%);
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  pointer-events: none;
  user-select: none;
  z-index: 2;
}

.pmt-video-watermark-logos {
  display: flex;
  align-items: center;
  gap: 10px;
}

.pmt-video-watermark-logos img {
  width: 64px;
  height: auto;
  opacity: 0.55;
  filter: drop-shadow(0 1px 2px rgba(0, 0, 0, 0.45));
}

.pmt-video-watermark-text {
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: rgba(255, 255, 255, 0.7);
  text-shadow: 0 1px 3px rgba(0, 0, 0, 0.65);
  white-space: nowrap;
  text-align: center;
}

.pmt-video-overlay {
  position: absolute;
  inset: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 12px;
  font-size: 12px;
  color: #94a3b8;
  background: rgba(2, 6, 23, 0.78);
  z-index: 3;
}

.pmt-video-overlay--limit {
  z-index: 4;
  gap: 10px;
}

.pmt-watch-timer {
  position: absolute;
  top: 10px;
  right: 10px;
  z-index: 2;
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 4px 8px;
  border-radius: 999px;
  background: rgba(2, 6, 23, 0.72);
  border: 1px solid #1e2c4f;
  font-size: 11px;
  font-weight: 600;
  color: #e2e8f0;
  pointer-events: none;
}

/* ================= RESPONSIVE ================= */
@media (max-width: 960px) {
  .cctv-hero-inner {
    flex-direction: column;
    text-align: center;
    gap: 14px;
    padding: 20px 16px;
  }

  .cctv-hero-title {
    font-size: 22px;
  }

  .cctv-hero-clock {
    width: 100%;
  }
}

@media (max-width: 600px) {
  .cctv-live-grid {
    grid-template-columns: 1fr;
  }
}
</style>
